import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

// MUI
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { type Dayjs } from 'dayjs';
import { ArrowLeft, RefreshCw, Save } from 'lucide';

// common
import Icon from 'common/components/Icon';
import useService from 'common/hook/useService';
import useGeneralHook from 'common/hook/useGeneralHook';
import useUserRoles from 'common/hook/useUserRoles';
import { SCHOOL_MEAL_CUSTOMER_TYPES } from 'common/constants';
import type { ClientFromKios } from 'api/apiClient';

// screen
import useCreateConfirmationCodes from 'modules/confirmation-code/services/useCreateConfirmationCodes';
import useConfirmationClients from 'modules/confirmation-code/services/useConfirmationClients';
import { CONFIRMATION_CODE_STATUS } from 'modules/confirmation-code/types';

const labelSx = { fontSize: 14, color: 'text.primary', mb: 0.5 } as const;

const ConfirmationCodeCreatePage = () => {
  const navigate = useNavigate();
  const service = useService();
  const { enqueueSnackbar } = useGeneralHook();
  const { clients } = useConfirmationClients();
  const { mutateAsync, isPending } = useCreateConfirmationCodes();
  // Chỉ CS-BAD được tạo mã (xlsx sheet 02 dòng 21 + grant BE). Vai trò khác gõ URL thẳng
  // vào /create → đưa về danh sách thay vì cho nhập rồi nhận 403 lúc lưu.
  const { isCSBAD } = useUserRoles();

  // Mã xác nhận chỉ dành cho phụ huynh trường CÓ thực đơn → chỉ chọn được Tiểu học + THCS
  // (Mầm non chỉ cung cấp thực phẩm, CQNN/KHAC không có thực đơn).
  const schoolOptions = useMemo(
    () => clients.filter((c) => SCHOOL_MEAL_CUSTOMER_TYPES.includes(String(c.type))),
    [clients]
  );
  // Tìm theo CẢ tên và mã CĐS, bỏ dấu — createFilterOptions của MUI, đúng cách QR NCC TPAT làm.
  const filterSchoolOptions = useMemo(
    () =>
      createFilterOptions<ClientFromKios>({
        stringify: (o) => `${o.name || ''} ${o.operationCode || ''}`.trim(),
      }),
    []
  );

  const [code, setCode] = useState('');
  const [targetOrgId, setTargetOrgId] = useState('');
  const [active, setActive] = useState(true);
  const [accessLimit, setAccessLimit] = useState(40);
  const [expiredDate, setExpiredDate] = useState<Dayjs | null>(null);
  const [description, setDescription] = useState('');

  const selectedSchool = useMemo(
    () => schoolOptions.find((c) => c.operationCode === targetOrgId) ?? null,
    [schoolOptions, targetOrgId]
  );

  // Sinh mã ngẫu nhiên khi mở form + nút "sinh lại".
  const generate = async () => {
    if (!service) return;
    const r = await service.ConfirmationCodeController_newCode();
    setCode(r?.data?.code ?? '');
  };
  useEffect(() => {
    if (!isCSBAD) return;
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCSBAD]);

  useEffect(() => {
    if (!isCSBAD) navigate('..', { replace: true });
  }, [isCSBAD, navigate]);

  // Route con của "confirmation-codes": index (list) | create | :id (detail).
  // Từ /create, đi lên 1 cấp là về list. KHÔNG nối thêm 'confirmation-codes'
  // (sẽ khớp route ':id' → màn "không tìm thấy").
  const backToList = () => navigate('..');

  const handleSave = async () => {
    if (!code || !targetOrgId || !expiredDate?.isValid()) {
      enqueueSnackbar({ message: 'Nhập đủ Mã code, Trường và Hạn sử dụng.', variant: 'error' });
      return;
    }
    if (accessLimit < 1) {
      enqueueSnackbar({ message: 'Số lượt truy cập phải >= 1.', variant: 'error' });
      return;
    }
    try {
      await mutateAsync({
        code,
        targetOrgId,
        accessLimit: Number(accessLimit),
        expiredAt: expiredDate.endOf('day').toISOString(), // hết hiệu lực cuối ngày đã chọn
        description: description.trim() || undefined,
        status: active ? CONFIRMATION_CODE_STATUS.ACTIVE : CONFIRMATION_CODE_STATUS.DISABLED,
      });
      backToList();
    } catch {
      /* snackbar đã báo trong hook (vd trùng mã → sinh lại) */
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      {/* Không có header trong card: Figma dùng tiêu đề trang "THÊM MÃ XÁC NHẬN" + breadcrumb
          do layout tự render (routes: confirmationCode.create). */}
      {/* 2 cột như Figma. Theme app KHÔNG có breakpoint 'md' (chỉ xs/mobile/tablet/laptop/
          desktop — setupTheme.ts) nên dùng 'tablet', nếu dùng 'md' sẽ bị bỏ qua → 1 cột. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', tablet: '1fr 1fr' },
          columnGap: 4,
          rowGap: 2.5,
          // KHÔNG giới hạn bề rộng: Figma cho 2 cột trải hết chiều rộng card.
        }}
      >
        {/* Cột trái */}
        <Box>
          <Typography sx={labelSx}>Mã code:</Typography>
          <TextField
            fullWidth
            size="small"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={generate}
                    title="Sinh mã ngẫu nhiên"
                    aria-label="Sinh mã ngẫu nhiên"
                  >
                    <Icon icon={RefreshCw} size={16} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box>
          <Typography sx={labelSx}>
            Số lượt truy cập: <Box component="span" sx={{ color: 'error.main' }}>*</Box>
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="number"
            inputProps={{ min: 1 }}
            value={accessLimit}
            onChange={(e) => setAccessLimit(Number(e.target.value))}
          />
        </Box>

        <Box>
          <Typography sx={labelSx}>
            Tên trường học: <Box component="span" sx={{ color: 'error.main' }}>*</Box>
          </Typography>
          {/* Gõ để tìm theo tên hoặc mã CĐS (giống ô chọn khách hàng ở QR NCC TPAT) — danh sách
              tới hàng trăm trường nên chọn bằng cách cuộn tay là không dùng được. */}
          <Autocomplete
            options={schoolOptions}
            value={selectedSchool}
            onChange={(_, picked) => setTargetOrgId(picked?.operationCode ?? '')}
            filterOptions={filterSchoolOptions}
            getOptionLabel={(o) => `${o.name} (${o.operationCode})`}
            isOptionEqualToValue={(o, v) => o.operationCode === v.operationCode}
            noOptionsText="Không tìm thấy trường phù hợp"
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="Tìm theo tên trường hoặc mã CĐS"
              />
            )}
          />
        </Box>
        <Box>
          <Typography sx={labelSx}>Hạn sử dụng:</Typography>
          <DatePicker
            value={expiredDate}
            onChange={(v) => setExpiredDate(v)}
            format="DD/MM/YYYY"
            minDate={dayjs()}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Box>

        <Box>
          <Typography sx={labelSx}>
            Trạng thái quản trị: <Box component="span" sx={{ color: 'error.main' }}>*</Box>
          </Typography>
          <Switch checked={active} onChange={(e) => setActive(e.target.checked)} color="success" />
        </Box>
        <Box>
          <Typography sx={labelSx}>Ghi chú</Typography>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={8}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
        <Button variant="outlined" color="primary" startIcon={<Icon icon={ArrowLeft} size={16} />} onClick={backToList}>
          Quay lại
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Icon icon={Save} size={16} />}
          onClick={handleSave}
          disabled={isPending}
        >
          Lưu dữ liệu
        </Button>
      </Box>
    </Paper>
  );
};

export default ConfirmationCodeCreatePage;
